import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  PerUserDevicePersistence,
  SettingDefinition,
  SettingsState,
  StoreBinding,
} from './settingTypes';

type ValueOwner<T> = { userId: string | undefined; saved: T };
type PendingSave = { timer: ReturnType<typeof setTimeout>; save: () => void };

const requireGlobalBinding = <T>(definition: SettingDefinition<T>): StoreBinding<T> => {
  const binding = definition.bindings?.global;
  if (!binding) {
    throw new Error(`Setting ${definition.key} has no global store to keep per user`);
  }
  return binding;
};

/**
 * Keeps a per-user device setting in step with the signed-in user: loads the
 * user's saved value into Redux whenever the user changes, and saves each
 * change to that user's storage (after `saveDelayMs`, when set, so a slider
 * drag saves once it settles). Mount once per setting, near the top of the app.
 */
export function usePerUserDeviceSettingSync<T>(
  definition: SettingDefinition<T>,
  persistence: PerUserDevicePersistence<T>,
  userId: string | undefined,
): void {
  const binding = requireGlobalBinding(definition);
  const { store, hydrate, saveDelayMs } = persistence;
  const isEqual = persistence.isEqual ?? ((a: T, b: T) => a === b);
  const dispatch = useDispatch();
  const value = useSelector(
    (state: SettingsState) => binding.select(state) ?? definition.defaultValue,
  );
  // The user whose value Redux holds, and what is in their storage. Null while
  // a user's value is loading, so nothing is saved to the wrong user.
  const ownerRef = React.useRef<ValueOwner<T> | null>(null);
  const pendingSaveRef = React.useRef<PendingSave | null>(null);

  const flushPendingSave = React.useCallback(() => {
    const pending = pendingSaveRef.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    pendingSaveRef.current = null;
    pending.save();
  }, []);

  React.useEffect(() => {
    flushPendingSave();
    ownerRef.current = null;
    let cancelled = false;
    void store.load(userId).then((loaded) => {
      if (cancelled) return;
      ownerRef.current = { userId, saved: loaded };
      dispatch(hydrate(loaded));
    });
    return () => {
      cancelled = true;
    };
    // The definition is a fixed registry entry: the user is what changes.
  }, [dispatch, flushPendingSave, userId]);

  React.useEffect(() => {
    const owner = ownerRef.current;
    if (!owner || isEqual(owner.saved, value)) return;
    const save = () => {
      ownerRef.current = ownerRef.current === owner ? { ...owner, saved: value } : ownerRef.current;
      void store.save(owner.userId, value);
    };
    if (saveDelayMs === undefined) {
      save();
      return;
    }
    if (pendingSaveRef.current) clearTimeout(pendingSaveRef.current.timer);
    pendingSaveRef.current = {
      timer: setTimeout(() => {
        pendingSaveRef.current = null;
        save();
      }, saveDelayMs),
      save,
    };
    // Only a change of value saves; the owner and options are read from refs.
  }, [value]);

  React.useEffect(() => flushPendingSave, [flushPendingSave]);
}
